// The apples: spawn points that roam the surface and periodically stop to
// produce a bug. Ports `SpawnPoint.cs` plus the `MoveStopSpawnTurn` half of the
// spawn-point state machine in `BugsModel.cs` (~950–1210) — walk a leg, stop,
// spawn a weighted bug kind, pivot 90 degrees, repeat.
//
// Pure logic: no WebGL, no DOM (00-architecture.md §1).

import type { EventBus } from "./events";
import { pickWeightedIndex, type Rng } from "./rng";
import { BugManager, type BugKind } from "./bugs";
import { createModifiers, type GlobalModifiers } from "./modifiers";
import { bodyTiles, Occupancy, restingTile } from "./occupancy";
import {
  advance,
  distanceToNextTile,
  frameAtHeading,
  poseToWorld,
  turnRight,
  DIR_UV,
  type SurfacePose,
  type Vec3,
} from "./surface";
import {
  BUG_KIND_ORDER,
  MOVE_LEG_SECONDS,
  SPAWN_ANIM_SECONDS,
  SPAWNPOINT_SPEED,
  TURN_SECONDS,
  type LevelDef,
} from "./defs";
import type { LevelSource } from "./levels";

/** Branded entity id (00-architecture.md §6). */
export type SpawnerId = number & { readonly __brand: "SpawnerId" };

/**
 * What the apple is doing. `progress` is normalised to [0, 1] over the state's
 * duration so the renderer can drive a clip time from it without knowing
 * durations; `remaining` is seconds left, because a leg can also end early
 * (an obstruction turns the apple aside).
 */
export type SpawnerState =
  | { kind: "moving"; remaining: number }
  | { kind: "spawning"; progress: number }
  | { kind: "turning"; progress: number };

export interface Spawner {
  readonly id: SpawnerId;
  pose: SurfacePose;
  state: SpawnerState;
  /** Seconds until this apple is due to produce its next bug. */
  nextSpawnIn: number;
}

/** Slack for landmark distances along a lane, in cells. */
const EPS = 1e-9;

/**
 * Exponential-ish delay around `mean`, the original's spawn cadence
 * (`unity-overview.md` §3 "Spawning"). Clamped to [0.5·mean, 3·mean] so a
 * draw near 0 or 1 cannot produce a burst or a minute-long silence.
 *
 * APPROX: `BugsModel.cs` ~1049 jitters ±10% uniformly around the mean and then
 * folds in the distance the apple can still cover, which needs the level-speed
 * machinery (out of scope). A clamped exponential gives the same mean with the
 * bursty feel the original gets from its two spawn points drifting apart.
 */
export function expDelay(rng: Rng, mean: number): number {
  const raw = -mean * Math.log(1 - rng());
  return Math.min(3 * mean, Math.max(0.5 * mean, raw));
}

/**
 * Weighted pick over `BUG_KIND_ORDER`, ported from `SpawnPointSpawnBug` — the
 * cumulative-interval walk itself is `pickWeightedIndex` (`rng.ts`), shared
 * with the power-up slot roll. Weights summing to < 100 leave a gap at the
 * top, and a roll landing in it spawns nothing — genuine original behavior,
 * see the footnote under `unity-overview.md` §5's level table.
 */
export function pickBugKind(rng: Rng, weights: readonly number[]): BugKind | null {
  const index = pickWeightedIndex(rng, weights);
  return index === null ? null : BUG_KIND_ORDER[index];
}

/**
 * Distance along the heading to the next lane centre. Apples stop only on
 * centres: that is where the original parks them (`CubeWalkerPrepareForStopIn
 * Center`) and it is what puts a freshly spawned bug on a lane centre.
 */
function distanceToNextCentre(p: SurfacePose): number {
  const along = p.dir === 0 || p.dir === 2 ? p.u : p.v;
  const frac = along - Math.floor(along);
  const forward = p.dir === 0 || p.dir === 1;
  const ahead = forward ? 0.5 - frac : frac - 0.5;
  return ahead > EPS ? ahead : ahead + 1;
}

/**
 * Snap both coordinates onto the tile centre the walker has just reached. Only
 * called on arrival at a centre, so the fractional parts are already 0.5 up to
 * accumulated float error — this stops that error from compounding over a lap.
 */
function snapToCentre(p: SurfacePose): SurfacePose {
  return { ...p, u: Math.floor(p.u) + 0.5, v: Math.floor(p.v) + 0.5 };
}

/**
 * World placement of an apple, for the renderer. While it pivots, the heading
 * is interpolated between the entry direction and the one it is turning into —
 * `pose.dir` only flips when the turn completes, so it cannot drive the body.
 */
export function spawnerFrame(s: Spawner): { pos: Vec3; normal: Vec3; forward: Vec3 } {
  if (s.state.kind !== "turning") return poseToWorld(s.pose);
  const from = DIR_UV[s.pose.dir];
  const to = DIR_UV[turnRight(s.pose).dir];
  const a = (s.state.progress * Math.PI) / 2;
  const c = Math.cos(a);
  const t = Math.sin(a);
  return frameAtHeading(
    s.pose.face,
    s.pose.u,
    s.pose.v,
    from[0] * c + to[0] * t,
    from[1] * c + to[1] * t,
  );
}

/**
 * Owns the level's spawn points. Ids count *down* from -1 so they can never
 * collide with `BugManager`'s, which count up from 1 — both live in the one
 * `Occupancy` (see `WalkerId` there), which is how a bug brakes for an apple
 * without either system knowing about the other.
 */
export class SpawnerManager {
  private readonly map = new Map<SpawnerId, Spawner>();
  private nextId = -1;

  constructor(
    private readonly events: EventBus,
    private readonly rng: Rng,
    private readonly bugs: BugManager,
    private readonly occupancy: Occupancy,
    // Plan 06 injected a single `LevelDef` here rather than hard-wiring LEVEL_1
    // precisely so plan 15 could swap it for the clock: the row is now read per
    // use, so a level crossing takes effect without anything being rebuilt.
    private readonly levels: LevelSource,
    // The double-spawn power-up (plan 16); defaults to 1x for the suites that
    // are not about power-ups.
    private readonly modifiers: GlobalModifiers = createModifiers(),
  ) {}

  /**
   * A freshly drawn spawn delay, divided by the double-spawn effect's rate —
   * the original's `TimeUntilNextSpawn /= _bugSpawnRate` (`BugsModel.cs:1053`)
   * applied where the delay is computed, so a rate change mid-countdown does
   * not retroactively bend a delay already drawn, as it does not there either.
   */
  private drawSpawnDelay(): number {
    return expDelay(this.rng, this.level.meanTimeBetweenBugSpawn) / this.modifiers.spawnRate;
  }

  /** The row governing spawning right now. */
  private get level(): LevelDef {
    return this.levels.current;
  }

  get spawners(): ReadonlyMap<SpawnerId, Spawner> {
    return this.map;
  }

  /** Place an apple at `pose`, walking, with its first spawn already scheduled. */
  add(pose: SurfacePose): Spawner {
    const id = this.nextId-- as SpawnerId;
    const spawner: Spawner = {
      id,
      pose,
      state: { kind: "moving", remaining: MOVE_LEG_SECONDS },
      nextSpawnIn: this.drawSpawnDelay(),
    };
    this.map.set(id, spawner);
    this.occupancy.reclaim(id, restingTile(pose));
    this.events.emit("spawnerAdded", { id });
    return spawner;
  }

  /**
   * Retire an apple — a level whose `AmountOfSpawnPoints` dropped
   * (`RemoveSpawnPoint`, `BugsModel.cs:44-49`). Its tile claims go with it, so
   * a walker braking behind it is released the same step.
   */
  remove(id: SpawnerId): void {
    if (!this.map.delete(id)) return;
    this.occupancy.releaseAll(id);
    this.events.emit("spawnerRemoved", { id });
  }

  /** Advance every apple by `dt` seconds. */
  update(dt: number): void {
    for (const s of this.map.values()) {
      const before = s.state.kind;
      this.step(s, dt);
      if (s.state.kind !== before) {
        this.events.emit("spawnerStateChanged", { id: s.id, state: s.state.kind });
      }
    }
  }

  /**
   * The one place spawner transitions live (00-architecture.md §5): an
   * exhaustive switch with a `never` check.
   */
  private step(s: Spawner, dt: number): void {
    // The countdown runs in every state, not just while walking: the original
    // credits the apple's stop against it explicitly (`TimeUntilNextSpawn -= 2f`
    // after spawning, `BugsModel.cs` ~1154). Ticking it throughout is the same
    // thing without hard-coding the stop's length.
    s.nextSpawnIn -= dt;
    const state = s.state;
    switch (state.kind) {
      case "moving": {
        state.remaining -= dt;
        this.walk(s, state, dt);
        return;
      }
      case "spawning": {
        state.progress += dt / SPAWN_ANIM_SECONDS;
        if (state.progress < 1) return;
        s.state = { kind: "turning", progress: 0 };
        return;
      }
      case "turning": {
        state.progress += dt / TURN_SECONDS;
        if (state.progress < 1) return;
        s.pose = turnRight(s.pose);
        this.startLeg(s);
        return;
      }
      default: {
        const unreachable: never = state;
        throw new Error(`unhandled spawner state ${JSON.stringify(unreachable)}`);
      }
    }
  }

  /** Resume walking, taking the tiles the moving body needs if they are free. */
  private startLeg(s: Spawner): void {
    s.state = { kind: "moving", remaining: MOVE_LEG_SECONDS };
    this.occupancy.reclaim(s.id, bodyTiles(s.pose));
  }

  /**
   * One step of a leg: walk at a constant speed, stopping at every lane centre
   * to see whether the leg is over. A tile it may not enter turns it right, the
   * same rule bugs follow — the original re-plans at block boundaries too.
   */
  private walk(s: Spawner, state: { kind: "moving"; remaining: number }, dt: number): void {
    // The level's `SpeedFactor` dilates apples too — it is applied to the
    // apple's leg budget in the original (`BugsModel.cs:1054`, `:1065`).
    let budget = SPAWNPOINT_SPEED * this.levels.speedFactor * dt;
    let pose = s.pose;
    while (budget > EPS) {
      const toCentre = distanceToNextCentre(pose);
      const step = Math.min(budget, toCentre, distanceToNextTile(pose));
      const next = advance(pose, step);
      if (!this.occupancy.reclaim(s.id, bodyTiles(next))) {
        s.pose = pose;
        s.state = { kind: "turning", progress: 0 };
        return;
      }
      pose = next;
      budget -= step;
      if (step < toCentre - EPS) continue;
      pose = snapToCentre(pose);
      if (state.remaining <= 0) {
        s.pose = pose;
        this.stop(s);
        return;
      }
    }
    s.pose = pose;
  }

  /**
   * End of a leg on a lane centre: produce a bug if one is due and the
   * population allows (`SetSpawningIfAmountOfBugsPermits`), otherwise just
   * pivot and walk on.
   */
  private stop(s: Spawner): void {
    if (s.nextSpawnIn > 0 || this.bugs.bugs.size >= this.level.maxBugs) {
      s.state = { kind: "turning", progress: 0 };
      this.occupancy.reclaim(s.id, restingTile(s.pose));
      return;
    }
    s.nextSpawnIn = this.drawSpawnDelay();
    s.state = { kind: "spawning", progress: 0 };
    // The apple yields its tile to the bug growing out of it — they genuinely
    // stand on the same spot in the original, and the bug needs the claim to be
    // able to walk away. The apple takes a tile again when it moves off.
    this.occupancy.releaseAll(s.id);
    const kind = pickBugKind(this.rng, this.level.occurrenceWeights);
    // A roll into the gap above the weights spawns nothing; the apple still
    // stops and turns, exactly as the original does with a null bug.
    if (kind !== null) this.bugs.spawn(kind, s.pose);
  }
}
